import posthog from 'posthog-js'
import { setGoogleAnalyticsCollection } from '@/lib/analytics/google-analytics'

/**
 * The visitor's consent decision, and the single place it is applied.
 *
 * One decision drives BOTH trackers. Keeping separate switches for PostHog and
 * Google Ads is how sites end up honouring a rejection in one and quietly
 * ignoring it in the other — the failure is invisible from the UI, and it is
 * the exact failure a regulator looks for.
 */
export type ConsentState = 'granted' | 'denied' | 'unset'

/**
 * Exported because components/GoogleTags.tsx reads this key SYNCHRONOUSLY, from
 * an inline script, to honour a standing rejection before GA's first hit. Two
 * spellings of the key would fail silently and in the direction that measures
 * people who said no.
 */
export const CONSENT_STORAGE_KEY = 'st.consent.analytics'
const STORAGE_KEY = CONSENT_STORAGE_KEY

export function readConsent(): ConsentState {
  if (typeof window === 'undefined') return 'unset'
  try {
    const v = window.localStorage.getItem(STORAGE_KEY)
    return v === 'granted' || v === 'denied' ? v : 'unset'
  } catch {
    // Storage blocked (private mode, hardened browser). Treat as undecided —
    // never as consent.
    return 'unset'
  }
}

function persist(state: Exclude<ConsentState, 'unset'>) {
  try {
    window.localStorage.setItem(STORAGE_KEY, state)
  } catch {
    // If we can't remember the choice we'll ask again next visit. Annoying,
    // but the alternative — assuming consent — is the one that isn't allowed.
  }
}

/**
 * Apply a decision to every tracker.
 *
 * Safe to call before either tracker has loaded: gtag queues through
 * `dataLayer`, and posthog-js buffers its own opt-in state.
 */
export function applyConsent(state: Exclude<ConsentState, 'unset'>) {
  const granted = state === 'granted'

  // --- Google Ads (Consent Mode v2) ---
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      ad_storage: granted ? 'granted' : 'denied',
      ad_user_data: granted ? 'granted' : 'denied',
      ad_personalization: granted ? 'granted' : 'denied',
      analytics_storage: granted ? 'granted' : 'denied',
    })
  }

  // --- Google Analytics 4 ---
  //
  // Consent Mode's `analytics_storage: denied` stops GA writing cookies but
  // still sends Google a cookieless ping per pageview. For a visitor who
  // actively pressed Reject that is not what the button appeared to promise, so
  // a rejection also throws GA's own kill switch and no hit leaves at all.
  //
  // Deliberately one-way: consent can switch GA OFF here but never ON. Turning
  // it back on is the path allowlist's job (components/GoogleTags.tsx) — the
  // banner is mounted app-wide, and accepting while sitting on an authenticated
  // route must not thereby start measuring that route.
  if (!granted) setGoogleAnalyticsCollection(false)

  // --- PostHog ---
  try {
    if (granted) {
      posthog.opt_in_capturing()
      // posthog-js captures $pageview at init. A visitor who arrives opted out
      // and then accepts has already had that first pageview dropped, so the
      // landing page — the most valuable step in the funnel — would be missing
      // from exactly the sessions that consented. Send it once, here.
      posthog.capture('$pageview')
    } else {
      posthog.opt_out_capturing()
    }
  } catch {
    // Analytics must never break the page. Same contract as lib/posthog-server.
  }
}

export function setConsent(state: Exclude<ConsentState, 'unset'>) {
  persist(state)
  applyConsent(state)
}
